import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Avatar,
  Card,
  CardContent,
  CardMedia,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';

const DRIVER_DOC_TEMPLATES = [
  { label: 'Select a template note...', value: '' },
  { label: 'Expired License', value: "Expired License: The uploaded driver's license has expired. Please upload a valid, unexpired license." },
  { label: 'Blurry Document', value: "Blurry Document: The image of your driver's license is blurry or illegible. Please take a clear photo showing all text and details clearly." },
  { label: 'Incorrect Document Type', value: "Incorrect Document Type: The uploaded document does not match the requested type. Please upload the correct driver's license photo." },
  { label: 'Name Mismatch', value: "Name Mismatch: The name on your driver's license does not match the name on your profile. Please contact support if this is a mistake." }
];

const VEHICLE_DOC_TEMPLATES = [
  { label: 'Select a template note...', value: '' },
  { label: 'Expired Insurance', value: "Expired Insurance: The insurance certificate has expired. Please upload a valid, active certificate of insurance." },
  { label: 'Plate Mismatch', value: "Plate Mismatch: The license plate number on the uploaded registration does not match the license plate in your vehicle details." },
  { label: 'Invalid Registration', value: "Incorrect Vehicle Registration: The uploaded document is not a valid vehicle registration certificate. Please upload the correct document." },
  { label: 'Blurry Vehicle Photos', value: "Unclear Photos: The uploaded vehicle photos are unclear or do not show the vehicle properly. Please upload clear photos of the front, back, and side of your vehicle." }
];

interface DriverVerification {
  id: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  email: string;
  phoneNumber: string;
  driverStatus: string;
  drivingLicenseUrl: string;
  drivingLicenseBackUrl?: string;
  licenseData: any;
  faceMatchScore: number;
  faceMatchResult: string;
  faceVerifiedAt: string;
  createdAt: string;
}

interface VehicleVerification {
  id: string;
  userId: string;
  user: {
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
  };
  make: { name: string };
  model: { name: string };
  year: number;
  color: string;
  licensePlate: string;
  verificationStatus: string;
  documents: any[];
  createdAt: string;
}

const VerificationDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');

  // Data
  const [driverVerifications, setDriverVerifications] = useState<DriverVerification[]>([]);
  const [vehicleVerifications, setVehicleVerifications] = useState<VehicleVerification[]>([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadVerifications();
  }, [activeTab]);

  const loadVerifications = async () => {
    setLoading(true);
    try {
      if (activeTab === 0) {
        // Load driver verifications
        const response = await apiService.get('/admin/driver-verifications', {
          status: 'PENDING,AI_VERIFIED'
        });
        if (response.success) {
          setDriverVerifications(response.data.verifications || []);
        }
      } else {
        // Load vehicle verifications
        const response = await apiService.get('/admin/vehicle-verifications', {
          status: 'PENDING'
        });
        if (response.success) {
          setVehicleVerifications(response.data.verifications || []);
        }
      }
    } catch (error: any) {
      console.error('Load verifications error:', error);
      setMessage('Failed to load verifications');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (item: any) => {
    setSelectedItem(item);
    setDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedItem) return;

    setLoading(true);
    try {
      if (activeTab === 0) {
        // Approve driver
        const response = await apiService.put(`/admin/approve-driver/${selectedItem.id}`);
        if (response.success) {
          setMessage('Driver approved successfully');
          setMessageType('success');
          setDialogOpen(false);
          loadVerifications();
        }
      } else {
        // Approve vehicle
        const response = await apiService.put(`/admin/approve-vehicle/${selectedItem.id}`);
        if (response.success) {
          setMessage('Vehicle approved successfully');
          setMessageType('success');
          setDialogOpen(false);
          loadVerifications();
        }
      }
    } catch (error: any) {
      console.error('Approve error:', error);
      setMessage(error.response?.data?.message || 'Failed to approve');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedItem || !rejectionReason.trim()) {
      setMessage('Please provide a rejection reason');
      setMessageType('error');
      return;
    }

    setLoading(true);
    try {
      if (activeTab === 0) {
        // Reject driver
        const response = await apiService.put(`/admin/reject-driver/${selectedItem.id}`, {
          reason: rejectionReason
        });
        if (response.success) {
          setMessage('Driver rejected');
          setMessageType('success');
          setDialogOpen(false);
          setRejectionReason('');
          loadVerifications();
        }
      } else {
        // Reject vehicle
        const response = await apiService.put(`/admin/reject-vehicle/${selectedItem.id}`, {
          reason: rejectionReason
        });
        if (response.success) {
          setMessage('Vehicle rejected');
          setMessageType('success');
          setDialogOpen(false);
          setRejectionReason('');
          loadVerifications();
        }
      }
    } catch (error: any) {
      console.error('Reject error:', error);
      setMessage(error.response?.data?.message || 'Failed to reject');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const renderDriverTable = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Driver</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>Face Match</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {driverVerifications.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography color="text.secondary">No pending verifications</Typography>
              </TableCell>
            </TableRow>
          ) : (
            driverVerifications.map((driver) => (
              <TableRow key={driver.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2">
                        {driver.preferredName || driver.firstName} {driver.lastName ? driver.lastName.charAt(0) + '.' : ''}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{driver.email}</TableCell>
                <TableCell>{driver.phoneNumber}</TableCell>
                <TableCell>
                  <Chip
                    label={`${driver.faceMatchScore}%`}
                    color={driver.faceMatchScore >= 90 ? 'success' : driver.faceMatchScore >= 85 ? 'warning' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={driver.driverStatus}
                    color={driver.driverStatus === 'AI_VERIFIED' ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {new Date(driver.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewDetails(driver)}
                  >
                    Review
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderVehicleTable = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Owner</TableCell>
            <TableCell>Vehicle</TableCell>
            <TableCell>License Plate</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Documents</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vehicleVerifications.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography color="text.secondary">No pending verifications</Typography>
              </TableCell>
            </TableRow>
          ) : (
            vehicleVerifications.map((vehicle) => (
              <TableRow key={vehicle.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2">
                        {vehicle.user.preferredName || vehicle.user.firstName} {vehicle.user.lastName ? vehicle.user.lastName.charAt(0) + '.' : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {vehicle.user.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  {vehicle.year} {vehicle.make.name} {vehicle.model.name}
                </TableCell>
                <TableCell>{vehicle.licensePlate}</TableCell>
                <TableCell>
                  <Chip label={vehicle.verificationStatus} color="warning" size="small" />
                </TableCell>
                <TableCell>
                  <Chip label={`${vehicle.documents.length} docs`} size="small" />
                </TableCell>
                <TableCell>
                  {new Date(vehicle.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewDetails(vehicle)}
                  >
                    Review
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderDetailDialog = () => {
    if (!selectedItem) return null;

    const isDriver = activeTab === 0;

    return (
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {isDriver ? 'Driver Verification' : 'Vehicle Verification'}
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {isDriver ? (
            <Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Driver Information
                    </Typography>
                    <Typography variant="body2">
                      Name: {selectedItem.preferredName || selectedItem.firstName} {selectedItem.lastName ? selectedItem.lastName.charAt(0) + '.' : ''}
                    </Typography>
                    <Typography variant="body2">
                      Email: {selectedItem.email}
                    </Typography>
                    <Typography variant="body2">
                      Phone: {selectedItem.phoneNumber}
                    </Typography>
                  </CardContent>
                </Card>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Card sx={{ flex: 1 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Face Match Result
                      </Typography>
                      <Chip
                        label={`${selectedItem.faceMatchScore}% Match`}
                        color={selectedItem.faceMatchScore >= 85 ? 'success' : 'error'}
                        sx={{ mb: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Result: {selectedItem.faceMatchResult}
                      </Typography>
                      {selectedItem.driverRejectionReason && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          <Typography variant="caption" fontWeight="bold" display="block">AI Rejection Reason:</Typography>
                          <Typography variant="caption">{selectedItem.driverRejectionReason}</Typography>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>

                  <Card sx={{ flex: 1 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        License Information
                      </Typography>
                      {selectedItem.licenseData && (
                        <>
                          <Typography variant="body2">
                            License #: {selectedItem.licenseData.licenseNumber || 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            Expiry: {selectedItem.licenseData.expiryDate ? new Date(selectedItem.licenseData.expiryDate).toLocaleDateString() : 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            Province: {selectedItem.licenseData.province || 'N/A'}
                          </Typography>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Box>

                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      License Photos (Front & Back)
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mt: 2 }}>
                      {selectedItem.drivingLicenseUrl && (
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">Front Side</Typography>
                          <CardMedia
                            component="img"
                            image={apiService.getBaseURL().replace('/api', '') + selectedItem.drivingLicenseUrl}
                            alt="Driving License Front"
                            sx={{ maxHeight: 300, objectFit: 'contain', mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                          />
                        </Box>
                      )}
                      {selectedItem.drivingLicenseBackUrl && (
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">Back Side</Typography>
                          <CardMedia
                            component="img"
                            image={apiService.getBaseURL().replace('/api', '') + selectedItem.drivingLicenseBackUrl}
                            alt="Driving License Back"
                            sx={{ maxHeight: 300, objectFit: 'contain', mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                          />
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Vehicle Information
                    </Typography>
                    <Typography variant="body2">
                      Vehicle: {selectedItem.year} {selectedItem.make.name} {selectedItem.model.name}
                    </Typography>
                    <Typography variant="body2">
                      Color: {selectedItem.color}
                    </Typography>
                    <Typography variant="body2">
                      License Plate: {selectedItem.licensePlate}
                    </Typography>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Documents ({selectedItem.documents.length})
                    </Typography>
                    {selectedItem.documents.map((doc: any, index: number) => (
                      <Box key={index} sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {doc.type.replace(/_/g, ' ')}
                          </Typography>
                          <Chip
                            label={doc.status}
                            size="small"
                            color={
                              doc.status === 'APPROVED' ? 'success' :
                              doc.status === 'REJECTED' ? 'error' :
                              doc.status === 'RESUBMISSION_REQUIRED' ? 'warning' : 'default'
                            }
                          />
                        </Box>
                        {doc.fileUrl && (
                          <CardMedia
                            component="img"
                            image={apiService.getBaseURL().replace('/api', '') + doc.fileUrl}
                            alt={doc.type}
                            sx={{ maxHeight: 400, objectFit: 'contain', mt: 1, mb: 2 }}
                          />
                        )}
                        {doc.reviewNotes && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="caption" fontWeight="bold" display="block">AI / Review Notes:</Typography>
                            <Typography variant="caption">{doc.reviewNotes}</Typography>
                          </Alert>
                        )}
                        <Divider />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Prepared Notes Template</InputLabel>
              <Select
                label="Prepared Notes Template"
                onChange={(e) => setRejectionReason(e.target.value as string)}
                defaultValue=""
              >
                {(isDriver ? DRIVER_DOC_TEMPLATES : VEHICLE_DOC_TEMPLATES).map((tmpl, idx) => (
                  <MenuItem key={idx} value={tmpl.value}>{tmpl.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Rejection Reason (required if rejecting)"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Provide a reason for rejection..."
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            disabled={loading || !rejectionReason.trim()}
            color="error"
            variant="outlined"
            startIcon={<RejectIcon />}
          >
            Reject
          </Button>
          <Button
            onClick={handleApprove}
            disabled={loading}
            color="success"
            variant="contained"
            startIcon={<ApproveIcon />}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Verification Dashboard
      </Typography>

      {message && (
        <Alert severity={messageType} sx={{ mb: 3 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<PersonIcon />}
            label="Driver Verifications"
            iconPosition="start"
          />
          <Tab
            icon={<CarIcon />}
            label="Vehicle Verifications"
            iconPosition="start"
          />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            {activeTab === 0 ? renderDriverTable() : renderVehicleTable()}
          </Box>
        )}
      </Paper>

      {renderDetailDialog()}
    </Box>
  );
};

export default VerificationDashboard;

